import StyledEngineProvider from "@mui/material/StyledEngineProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import "keen-slider/keen-slider.min.css";
import { StrictMode } from "react";
import * as ReactDOM from "react-dom/client";
import "react-lazy-load-image-component/src/effects/blur.css";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import "simplebar-react/dist/simplebar.min.css";

import App from "./App";
import HealthGate from "./components/Core/HealthGate";
import { HEALTH_CHECK_QUERY_KEY } from "./components/Core/HealthGate/data";
import "./index.css";
import { initGtag } from "./lib/gtag";
import { initPwa } from "./lib/pwa";
import { queryClient } from "./lib/queryClient";
import { restoreSession } from "./lib/session";
import { telegramSdkReady } from "./lib/telegram";
import { i18nReady } from "./locales/i18n";
import reportWebVitals from "./reportWebVitals";
import { getCheck } from "./services";
import { persister, store } from "./store";
import { AppTheme } from "./theme";
import { captureAffiliateCodeFromUrl, captureReferralCodeFromUrl } from "./utils";
import { captureAttributionFromUrl, capturePromoCodeFromUrl } from "./utils/attribution";

captureReferralCodeFromUrl();
captureAffiliateCodeFromUrl();
captureAttributionFromUrl();
capturePromoCodeFromUrl();
initPwa();
initGtag();

// HealthGate waits on both; started here they run while the language chunk downloads.
// retry as the query has it: a prefetch does not retry by default, and HealthGate would show the error page.
void queryClient.prefetchQuery({ queryKey: HEALTH_CHECK_QUERY_KEY, queryFn: getCheck, retry: 1 });
void restoreSession();

const rootElement = document.getElementById("root");
const root = ReactDOM.createRoot(rootElement!);

// Wait for the visitor's language bundle so the first paint is already in their language,
// and inside Telegram for the Mini App SDK, which the providers read as they mount.
void Promise.all([i18nReady, telegramSdkReady]).then(() =>
  root.render(
    <StrictMode>
      <StyledEngineProvider injectFirst>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persister}>
            <QueryClientProvider client={queryClient}>
              <AppTheme>
                <HealthGate>
                  <App />
                </HealthGate>
              </AppTheme>
            </QueryClientProvider>
          </PersistGate>
        </Provider>
      </StyledEngineProvider>
    </StrictMode>,
  ),
);

reportWebVitals();
